/* global gdAlertify */
import React, { useState, useEffect, useMemo } from "react";
import axiosInstance from "../config/axiosConfig";

const DISTANCIAS = [
    { value: 'cercana', label: 'Cercana', badge: 'success' },
    { value: 'lejana', label: 'Lejana', badge: 'warning' },
    { value: 'muy_lejana', label: 'Muy Lejana', badge: 'danger' },
];

const badgeClass = { cercana: 'success', lejana: 'warning', muy_lejana: 'danger' };

const comunaFormVacio = {
    id: null,
    nombre: '',
    codigo_comuna: '',
    codigo_region: '',
    region: '',
    provincia: '',
    distancia_visita: 'cercana',
};

const Comunas = () => {
    const [comunas, setComunas] = useState([]);
    const [filtro, setFiltro] = useState('');
    const [filtroDistancia, setFiltroDistancia] = useState('');
    const [cargando, setCargando] = useState(true);
    const [guardando, setGuardando] = useState(null);
    const [guardandoComuna, setGuardandoComuna] = useState(false);
    const [modalAbierto, setModalAbierto] = useState(false);
    const [comunaForm, setComunaForm] = useState(comunaFormVacio);

    useEffect(() => { cargarComunas(); }, []);

    const cargarComunas = async () => {
        setCargando(true);
        try {
            const { data } = await axiosInstance.get('/params/comunas');
            if (!data.error) setComunas(data.comunas || []);
            else gdAlertify.notiError(data.message || 'Error al cargar comunas.');
        } catch (err) {
            console.error(err);
            gdAlertify.notiError('Error al conectar con el servidor.');
        } finally {
            setCargando(false);
        }
    };

    const handleDistanciaChange = async (idComuna, distancia) => {
        setGuardando(idComuna);
        try {
            const { data } = await axiosInstance.post('/params/comunas/distancia', {
                idComuna,
                distancia,
            });
            if (!data.error) {
                setComunas(prev =>
                    prev.map(c => c.id === idComuna ? { ...c, distancia_visita: distancia } : c)
                );
                gdAlertify.notiSuccess(data.message || 'Distancia actualizada.');
            } else {
                gdAlertify.notiError(data.message || 'Error al actualizar.');
            }
        } catch (err) {
            console.error(err);
            gdAlertify.notiError('Error al conectar con el servidor.');
        } finally {
            setGuardando(null);
        }
    };

    const abrirModalNueva = () => {
        setComunaForm(comunaFormVacio);
        setModalAbierto(true);
    };

    const abrirModalEditar = (comuna) => {
        setComunaForm({
            id: comuna.id,
            nombre: comuna.nombre || '',
            codigo_comuna: comuna.codigo_comuna || '',
            codigo_region: comuna.codigo_region || '',
            region: comuna.region || '',
            provincia: comuna.provincia || '',
            distancia_visita: comuna.distancia_visita || 'cercana',
        });
        setModalAbierto(true);
    };

    const cerrarModal = () => {
        if (!guardandoComuna) setModalAbierto(false);
    };

    const handleComunaFormChange = (campo, valor) => {
        setComunaForm(prev => ({ ...prev, [campo]: valor }));
    };

    const guardarComuna = async (event) => {
        event.preventDefault();

        if (!comunaForm.nombre.trim()) {
            gdAlertify.alert('Debe ingresar el nombre de la comuna.');
            return;
        }

        setGuardandoComuna(true);
        try {
            const { data } = await axiosInstance.post('/params/comunas/guardar', {
                ...comunaForm,
                nombre: comunaForm.nombre.trim(),
                codigo_comuna: comunaForm.codigo_comuna.trim(),
                codigo_region: comunaForm.codigo_region.trim(),
                region: comunaForm.region.trim(),
                provincia: comunaForm.provincia.trim(),
            });

            if (!data.error) {
                gdAlertify.notiSuccess(data.message || 'Comuna guardada.');
                setModalAbierto(false);
                cargarComunas();
            } else {
                gdAlertify.notiError(data.message || 'No se pudo guardar la comuna.');
            }
        } catch (err) {
            console.error(err);
            gdAlertify.notiError('Error al conectar con el servidor.');
        } finally {
            setGuardandoComuna(false);
        }
    };

    const normalize = (s) =>
        (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    const comunasFiltradas = useMemo(() => {
        const q = normalize(filtro);
        return comunas.filter(c => {
            const coincideTexto = !q || [c.nombre, c.provincia, c.region].some(v => normalize(v).includes(q));
            const coincideDistancia = !filtroDistancia || c.distancia_visita === filtroDistancia;
            return coincideTexto && coincideDistancia;
        });
    }, [comunas, filtro, filtroDistancia]);

    const conteos = useMemo(() => {
        const acc = { cercana: 0, lejana: 0, muy_lejana: 0 };
        comunas.forEach(c => { if (acc[c.distancia_visita] !== undefined) acc[c.distancia_visita]++; });
        return acc;
    }, [comunas]);

    return (
        <div className="container-fluid mt-4">
            <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <div>
                        <h4 className="mb-0">Comunas - Distancia de Visita</h4>
                        <span className="text-muted small">{comunasFiltradas.length} / {comunas.length} comunas</span>
                    </div>
                    <button type="button" className="btn btn-primary btn-sm" onClick={abrirModalNueva}>
                        Nueva comuna
                    </button>
                </div>
                <div className="card-body">
                    <div className="row g-2 mb-3">
                        {DISTANCIAS.map(d => (
                            <div className="col-auto" key={d.value}>
                                <span className={`badge bg-${d.badge} fs-6 px-3 py-2`}>
                                    {d.label}: {conteos[d.value]}
                                </span>
                            </div>
                        ))}
                    </div>

                    <div className="row mb-3 g-2">
                        <div className="col-md-4">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Buscar por nombre, provincia o region..."
                                value={filtro}
                                onChange={e => setFiltro(e.target.value)}
                            />
                        </div>
                        <div className="col-md-3">
                            <select
                                className="form-select"
                                value={filtroDistancia}
                                onChange={e => setFiltroDistancia(e.target.value)}
                            >
                                <option value="">Todas las distancias</option>
                                {DISTANCIAS.map(d => (
                                    <option key={d.value} value={d.value}>{d.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {cargando ? (
                        <div className="text-center py-5 text-muted">Cargando comunas...</div>
                    ) : (
                        <div style={{ maxHeight: 600, overflowY: 'auto' }}>
                            <table className="table table-bordered table-sm gd-table-p5">
                                <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                    <tr>
                                        <th>Comuna</th>
                                        <th>Provincia</th>
                                        <th>Region</th>
                                        <th style={{ width: 200 }}>Distancia Visita</th>
                                        <th style={{ width: 100 }}>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {comunasFiltradas.map(c => (
                                        <tr key={c.id}>
                                            <td><b>{c.nombre}</b></td>
                                            <td>{c.provincia}</td>
                                            <td>{c.region}</td>
                                            <td>
                                                <select
                                                    className={`form-select form-select-sm border-${badgeClass[c.distancia_visita] || 'secondary'}`}
                                                    value={c.distancia_visita || 'cercana'}
                                                    disabled={guardando === c.id}
                                                    onChange={e => handleDistanciaChange(c.id, e.target.value)}
                                                >
                                                    {DISTANCIAS.map(d => (
                                                        <option key={d.value} value={d.value}>{d.label}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary btn-sm"
                                                    onClick={() => abrirModalEditar(c)}
                                                >
                                                    Editar
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {comunasFiltradas.length === 0 && (
                                <div className="text-center text-muted py-4">No se encontraron comunas.</div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {modalAbierto && (
                <div className="modal d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                        <form className="modal-content" onSubmit={guardarComuna}>
                            <div className="modal-header">
                                <h5 className="modal-title">{comunaForm.id ? 'Editar comuna' : 'Nueva comuna'}</h5>
                                <button type="button" className="btn-close" onClick={cerrarModal} disabled={guardandoComuna}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Nombre</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={comunaForm.nombre}
                                            onChange={e => handleComunaFormChange('nombre', e.target.value)}
                                            maxLength={120}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">Codigo comuna</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={comunaForm.codigo_comuna}
                                            onChange={e => handleComunaFormChange('codigo_comuna', e.target.value)}
                                            maxLength={20}
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">Codigo region</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={comunaForm.codigo_region}
                                            onChange={e => handleComunaFormChange('codigo_region', e.target.value)}
                                            maxLength={20}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Provincia</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={comunaForm.provincia}
                                            onChange={e => handleComunaFormChange('provincia', e.target.value)}
                                            maxLength={120}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Region</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={comunaForm.region}
                                            onChange={e => handleComunaFormChange('region', e.target.value)}
                                            maxLength={120}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Distancia visita</label>
                                        <select
                                            className="form-select"
                                            value={comunaForm.distancia_visita}
                                            onChange={e => handleComunaFormChange('distancia_visita', e.target.value)}
                                        >
                                            {DISTANCIAS.map(d => (
                                                <option key={d.value} value={d.value}>{d.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={cerrarModal} disabled={guardandoComuna}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={guardandoComuna}>
                                    {guardandoComuna ? 'Guardando...' : 'Guardar'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Comunas;
